import { useEffect, useState, useMemo } from "react";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, Loader2, Trash2, Download, FolderOpen, Upload, FileText, History, Filter } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

const CATEGORY_STYLES = {
  contratto: "bg-blue-50 text-blue-700 border-blue-200",
  preventivo: "bg-purple-50 text-purple-700 border-purple-200",
  fattura: "bg-emerald-50 text-emerald-700 border-emerald-200",
  disciplinare: "bg-amber-50 text-amber-700 border-amber-200",
  progetto: "bg-indigo-50 text-indigo-700 border-indigo-200",
  pratica: "bg-cyan-50 text-cyan-700 border-cyan-200",
  durc: "bg-orange-50 text-orange-700 border-orange-200",
  polizza: "bg-rose-50 text-rose-700 border-rose-200",
  generico: "bg-neutral-100 text-neutral-700 border-neutral-200",
};

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Documents() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState({ category: "", client_id: "", project_id: "", supplier_name: "" });
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.category) params.category = filter.category;
      if (filter.client_id) params.client_id = filter.client_id;
      if (filter.project_id) params.project_id = filter.project_id;
      if (filter.supplier_name) params.supplier_name = filter.supplier_name;
      const [m, l, c, p] = await Promise.all([
        api.get("/documents/meta"),
        api.get("/documents", { params }),
        api.get("/clients"),
        api.get("/projects"),
      ]);
      setMeta(m.data); setItems(l.data); setClients(c.data); setProjects(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.category, filter.client_id, filter.project_id, filter.supplier_name]);

  const openUpload = (replaces = null) => {
    setReplaceTarget(replaces);
    setUploadForm({
      file: null,
      title: replaces?.title || "",
      category: replaces?.category || "generico",
      client_id: replaces?.client_id || "",
      project_id: replaces?.project_id || "",
      supplier_name: replaces?.supplier_name || "",
      expiry_date: replaces?.expiry_date?.slice(0, 10) || "",
      description: replaces?.description || "",
      tags: (replaces?.tags || []).join(", "),
    });
    setUploadOpen(true);
  };

  const doUpload = async () => {
    if (!uploadForm.file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      if (uploadForm.title) fd.append("title", uploadForm.title);
      fd.append("category", uploadForm.category);
      if (uploadForm.client_id) fd.append("client_id", uploadForm.client_id);
      if (uploadForm.project_id) fd.append("project_id", uploadForm.project_id);
      if (uploadForm.supplier_name) fd.append("supplier_name", uploadForm.supplier_name);
      if (uploadForm.expiry_date) fd.append("expiry_date", uploadForm.expiry_date);
      if (uploadForm.description) fd.append("description", uploadForm.description);
      if (uploadForm.tags) fd.append("tags", uploadForm.tags);
      if (replaceTarget?.id) fd.append("replaces_id", replaceTarget.id);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(replaceTarget ? "Nuova versione caricata" : "Documento caricato");
      setUploadOpen(false); setReplaceTarget(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore upload"); }
    finally { setUploading(false); }
  };

  const download = async (doc) => {
    try {
      const t = localStorage.getItem("crm_token");
      const r = await fetch(`${API}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error("errore");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo documento? Non recuperabile.")) return;
    await api.delete(`/documents/${id}`); toast.success("Eliminato"); await load();
  };

  const openVersions = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/versions`);
      setVersions(r.data); setVersionsOpen(true);
    } catch { toast.error("Errore caricamento versioni"); }
  };

  const expiringCount = useMemo(() => {
    const now = new Date();
    return items.filter((d) => {
      if (!d.expiry_date) return false;
      const diff = (new Date(d.expiry_date) - now) / (1000 * 60 * 60 * 24);
      return diff <= 15 && diff >= -1;
    }).length;
  }, [items]);

  if (loading || !meta) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label flex items-center gap-1.5"><FolderOpen className="h-3 w-3" /> Archivio</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Documenti</h1>
          <p className="text-sm text-neutral-500 mt-2">
            {items.length} documenti · versioning attivo
            {expiringCount > 0 && <> · <strong className="text-orange-700">{expiringCount} in scadenza</strong></>}
          </p>
        </div>
        <Button onClick={() => openUpload(null)} data-testid="upload-doc-btn" className="brand-bg hover:opacity-90">
          <Upload className="h-4 w-4 mr-1.5" /> Carica documento
        </Button>
      </header>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 p-3 flex items-center gap-3 flex-wrap">
        <Filter className="h-4 w-4 text-neutral-500" />
        <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} data-testid="filter-category" className="border border-neutral-300 px-2 py-1.5 text-xs">
          <option value="">Tutte le categorie</option>
          {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={filter.client_id} onChange={(e) => setFilter({ ...filter, client_id: e.target.value })} className="border border-neutral-300 px-2 py-1.5 text-xs">
          <option value="">Tutti i clienti</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filter.project_id} onChange={(e) => setFilter({ ...filter, project_id: e.target.value })} className="border border-neutral-300 px-2 py-1.5 text-xs">
          <option value="">Tutti i progetti</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <Input placeholder="Fornitore…" value={filter.supplier_name} onChange={(e) => setFilter({ ...filter, supplier_name: e.target.value })} className="h-8 w-40 text-xs" />
        {(filter.category || filter.client_id || filter.project_id || filter.supplier_name) && (
          <button onClick={() => setFilter({ category: "", client_id: "", project_id: "", supplier_name: "" })} className="text-xs text-neutral-500 underline">Reset</button>
        )}
      </div>

      <div className="border border-neutral-200 bg-white">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">
            <FolderOpen className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
            Nessun documento. Carica contratti, DURC, polizze, elaborati progettuali.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="p-3 mono-label">Nome</th>
                <th className="p-3 mono-label">Categoria</th>
                <th className="p-3 mono-label">Cliente / Progetto</th>
                <th className="p-3 mono-label">Fornitore / Scadenza</th>
                <th className="p-3 mono-label">Size</th>
                <th className="p-3 mono-label">Versione</th>
                <th className="p-3 mono-label text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((d) => {
                const catLabel = meta.categories.find((c) => c.key === d.category)?.label || d.category;
                const isExpired = d.expiry_date && new Date(d.expiry_date) < new Date();
                const soonExpiring = d.expiry_date && !isExpired && ((new Date(d.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 15;
                return (
                  <tr key={d.id} className="hover:bg-neutral-50" data-testid={`doc-${d.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-neutral-400 shrink-0" />
                        <div>
                          <div className="font-medium">{d.title || d.name}</div>
                          <div className="text-xs text-neutral-500">{d.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-block font-mono text-[10px] font-semibold px-1.5 py-1 border ${CATEGORY_STYLES[d.category] || CATEGORY_STYLES.generico}`}>
                        {catLabel}
                      </span>
                    </td>
                    <td className="p-3 text-xs">
                      {d.client_name && <div>{d.client_name}</div>}
                      {d.project_title && <div className="text-neutral-500">{d.project_title}</div>}
                      {!d.client_name && !d.project_title && <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="p-3 text-xs">
                      {d.supplier_name && <div className="text-neutral-700">{d.supplier_name}</div>}
                      {d.expiry_date && (
                        <div className={`font-mono text-[11px] ${isExpired ? "text-red-700 font-semibold" : soonExpiring ? "text-orange-700" : "text-neutral-500"}`}>
                          {isExpired ? "SCADUTO " : "Scad. "}{fmtDate(d.expiry_date)}
                        </div>
                      )}
                      {!d.supplier_name && !d.expiry_date && <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="p-3 font-mono text-xs text-neutral-500">{fmtBytes(d.size_bytes)}</td>
                    <td className="p-3 font-mono text-xs">
                      v{d.version}
                      <span className="text-[10px] text-neutral-400 ml-1">{d.storage_kind === "emergent_object" ? "·obj" : "·db"}</span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => download(d)} title="Scarica" data-testid={`dl-${d.id}`} className="p-1.5 hover:bg-neutral-100">
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => openUpload(d)} title="Carica nuova versione" data-testid={`new-ver-${d.id}`} className="p-1.5 hover:bg-blue-50 hover:text-blue-700">
                          <Upload className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => openVersions(d)} title="Storia versioni" data-testid={`ver-${d.id}`} className="p-1.5 hover:bg-neutral-100">
                          <History className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => remove(d.id)} data-testid={`del-doc-${d.id}`} className="p-1.5 hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{replaceTarget ? `Nuova versione di: ${replaceTarget.title || replaceTarget.name}` : "Carica documento"}</DialogTitle>
          </DialogHeader>
          {uploadForm && (
            <div className="space-y-3">
              <div>
                <label className="mono-label">File</label>
                <input type="file" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })} data-testid="doc-file-input" className="w-full border border-neutral-300 px-3 py-2 text-sm bg-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Titolo</label>
                  <Input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} data-testid="doc-title" placeholder="Nome descrittivo" />
                </div>
                <div>
                  <label className="mono-label">Categoria</label>
                  <select value={uploadForm.category} onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })} data-testid="doc-category" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Cliente</label>
                  <select value={uploadForm.client_id} onChange={(e) => setUploadForm({ ...uploadForm, client_id: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">—</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Progetto</label>
                  <select value={uploadForm.project_id} onChange={(e) => setUploadForm({ ...uploadForm, project_id: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">—</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
              </div>
              {(uploadForm.category === "durc" || uploadForm.category === "polizza") && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 border border-amber-200">
                  <div>
                    <label className="mono-label">Fornitore</label>
                    <Input value={uploadForm.supplier_name} onChange={(e) => setUploadForm({ ...uploadForm, supplier_name: e.target.value })} data-testid="doc-supplier" placeholder="es. Impresa Rossi SRL" />
                  </div>
                  <div>
                    <label className="mono-label">Scadenza</label>
                    <Input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })} data-testid="doc-expiry" />
                  </div>
                </div>
              )}
              {uploadForm.category !== "durc" && uploadForm.category !== "polizza" && (
                <div>
                  <label className="mono-label">Scadenza (opz.)</label>
                  <Input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })} />
                </div>
              )}
              <div>
                <label className="mono-label">Note</label>
                <Textarea rows={2} value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
              </div>
              <div>
                <label className="mono-label">Tag (separati da virgola)</label>
                <Input value={uploadForm.tags} onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })} placeholder="es. 2026, elettrico" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Annulla</Button>
            <Button onClick={doUpload} disabled={uploading} data-testid="doc-upload-save" className="brand-bg hover:opacity-90">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Carica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Storia versioni</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v.id} className="border border-neutral-200 p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{v.title || v.name}</div>
                  <div className="text-xs text-neutral-500 flex items-center gap-2">
                    <span className="font-mono">v{v.version}</span>
                    <span>·</span>
                    <span>{fmtDate(v.created_at)}</span>
                    <span>·</span>
                    <span>{fmtBytes(v.size_bytes)}</span>
                  </div>
                </div>
                <button onClick={() => download(v)} className="p-1.5 hover:bg-neutral-100"><Download className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
